import React, { useState } from 'react';
import styled from 'styled-components';
import { Container } from '../styles/components';
import SectionNumber from './SectionNumber';

const TESTIMONIALS_DATA = [
  {
    isHero: true,
    quote: "I have learned how powerful an impression a good slide deck can make. We closed on $80m this Friday and I got heartfelt compliments from professional investors who have seen and heard a million pitches.",
    author: "Hugh Rienhoff Jr",
    role: "CEO, Imago Biosciences",
    image: "/assets/testimonials/hugh.jpg"
  },
  {
    quote: "As a company establishing itself in a competitive, highly-regulated industry it was crucial we presented ourselves as professional, innovative and precise. Ellie has added value throughout: from early work on our pitch decks and a $37m fundraising round, to a website, messaging and collateral. Her design and editorial acumen is incredible. I would thoroughly recommend her work.",
    author: "Tom Cavanagh",
    role: "CEO, Oxular",
    image: "/assets/testimonials/tom.jpg"
  },
  {
    quote: "You have helped me clarify more in a couple of months than I have been able to do in years. No kidding. You've quietly gotten me more and more focused. You also got to know me, and we were able to extract a style and messaging that just made sense. You can't be your own mirror, you know. So I needed you to see my business so I could see my business.",
    author: "Nancy Hand",
    role: "CEO, Breaking Bread",
    image: "/assets/testimonials/nancy.jpg"
  },
  {
    quote: "Right from the start, Ellie stretched my thinking and challenged me. She's made strategic suggestions, been a superb sounding board and never rested until the output was what I needed. The feedback from prospects has been universally positive and it has opened doors and built credibility for the business.",
    author: "Jeremy Stockdale",
    role: "CEO, Ylead",
    image: "/assets/testimonials/jeremy.jpg"
  },
  {
    quote: "Ellie is great to work with - smart, flexible and creative. She consulted on and designed stunning decks for major events and client pitches. The pressure was always on, but she never found a problem she couldn't solve. Her work is incredible.",
    author: "Jen Lloyd",
    role: "Director of Comms, NBCUniversal International",
    image: "/assets/testimonials/jen.jpg"
  }
];

const SectionWrapper = styled.section`
  height: 100svh;
  min-height: 800px;
  background: ${props => props.theme.colors.charcoal};
  color: ${props => props.theme.colors.bone};
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: auto;
    min-height: 100svh;
    padding: ${props => props.theme.spacing.xxl} 0;
  }
`;

const ContentGrid = styled(Container)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xxxl};
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  height: 100%;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

// Left Column: Hero
const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: relative;
  z-index: 2;
`;

const Headline = styled.h2`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-size: ${props => props.theme.typography.sizes.h2};
  color: ${props => props.theme.colors.bone};
  margin-bottom: ${props => props.theme.spacing.xl};
  line-height: 1.1;
  max-width: 20ch;
`;

const HeroCard = styled.div`
  background: transparent;
  border: 1px solid rgba(234, 230, 225, 0.2); // Bone with opacity
  border-radius: ${props => props.theme.radii.lg};
  padding: ${props => props.theme.spacing.xxl};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
  position: relative;

  &::before {
    content: '"';
    position: absolute;
    top: -40px;
    left: 20px;
    font-size: 8rem;
    color: ${props => props.theme.colors.terracotta};
    opacity: 0.3;
    font-family: ${props => props.theme.typography.highlight.fontFamily};
    line-height: 1;
    pointer-events: none;
  }
`;

const HeroQuote = styled.blockquote`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-size: ${props => props.theme.typography.sizes.h3};
  line-height: 1.3;
  color: ${props => props.theme.colors.bone};
  margin: 0;
  position: relative;
  z-index: 1;
`;

// Right Column: Vertical Slider
const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: auto;
  }
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const TestimonialCard = styled.div`
  background: transparent;
  border: 1px solid rgba(234, 230, 225, 0.1);
  border-radius: ${props => props.theme.radii.card};
  padding: ${props => props.theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  transition: ${props => props.theme.transitions.default};
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  &:hover {
    border-color: rgba(234, 230, 225, 0.3);
    background: rgba(234, 230, 225, 0.02);
  }
`;

const CardQuote = styled.p`
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: ${props => props.theme.typography.sizes.body};
  line-height: 1.6;
  color: ${props => props.theme.colors.bone};
  margin: 0;
  opacity: 0.9;
`;

const AuthorBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-top: auto;
`;

const AuthorImage = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.blueMidGrey};
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-weight: 700;
  font-size: ${props => props.theme.typography.sizes.body};
  color: ${props => props.theme.colors.terracotta};
`;

const AuthorRole = styled.span`
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: ${props => props.theme.typography.sizes.caption};
  color: ${props => props.theme.colors.bone};
  opacity: 0.7;
`;

const Controls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
`;

const ControlButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.blueMidGrey};
  color: ${props => props.theme.colors.bone};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  opacity: ${props => props.disabled ? 0.3 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};

  &:hover {
    background: ${props => props.theme.colors.bone};
    color: ${props => props.theme.colors.charcoal};
    border-color: ${props => props.theme.colors.bone};
  }
`;

const ArrowIcon = ({ direction }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {direction === 'up' ? (
      <path d="M18 15l-6-6-6 6" />
    ) : (
      <path d="M6 9l6 6 6-6" />
    )}
  </svg>
);

const Testimonials = () => {
  const [startIndex, setStartIndex] = useState(0);
  const heroTestimonial = TESTIMONIALS_DATA[0];
  const supportingTestimonials = TESTIMONIALS_DATA.slice(1);
  const ITEMS_PER_PAGE = 2;

  const nextSlide = () => {
    if (startIndex + ITEMS_PER_PAGE < supportingTestimonials.length) {
      setStartIndex(prev => prev + ITEMS_PER_PAGE);
    } else {
      setStartIndex(0); // Loop back
    }
  };

  const prevSlide = () => {
    if (startIndex - ITEMS_PER_PAGE >= 0) {
      setStartIndex(prev => prev - ITEMS_PER_PAGE);
    } else {
      // Go to last page
      const remainder = supportingTestimonials.length % ITEMS_PER_PAGE;
      const lastStart = remainder === 0
        ? supportingTestimonials.length - ITEMS_PER_PAGE
        : supportingTestimonials.length - remainder;
      setStartIndex(lastStart);
    }
  };

  const visibleTestimonials = supportingTestimonials.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <SectionWrapper id="testimonials">
      <ContentGrid>

        {/* Left Column */}
        <LeftColumn>
          <Headline>
            What it looks like when a plan comes together
          </Headline>

          <HeroCard>
            <HeroQuote>
              {heroTestimonial.quote}
            </HeroQuote>
            <AuthorBlock>
              <AuthorImage src={heroTestimonial.image} alt={heroTestimonial.author} />
              <AuthorInfo>
                <AuthorName style={{ fontSize: '1.25rem' }}>{heroTestimonial.author}</AuthorName>
                <AuthorRole style={{ fontSize: '1rem' }}>{heroTestimonial.role}</AuthorRole>
              </AuthorInfo>
            </AuthorBlock>
          </HeroCard>
        </LeftColumn>

        {/* Right Column */}
        <RightColumn>
          <SliderContainer>
            {visibleTestimonials.map((t, index) => (
              <TestimonialCard key={startIndex + index}>
                <CardQuote>"{t.quote}"</CardQuote>
                <AuthorBlock>
                  <AuthorImage src={t.image} alt={t.author} />
                  <AuthorInfo>
                    <AuthorName>{t.author}</AuthorName>
                    <AuthorRole>{t.role}</AuthorRole>
                  </AuthorInfo>
                </AuthorBlock>
              </TestimonialCard>
            ))}
          </SliderContainer>

          <Controls>
            <ControlButton onClick={prevSlide} aria-label="Previous testimonials">
              <ArrowIcon direction="up" />
            </ControlButton>
            <ControlButton onClick={nextSlide} aria-label="Next testimonials">
              <ArrowIcon direction="down" />
            </ControlButton>
          </Controls>
        </RightColumn>

      </ContentGrid>

      <SectionNumber number={6} />
    </SectionWrapper>
  );
};

export default Testimonials;
